import { getColor } from '@chakra-ui/theme-tools';

import { theme } from '@frontend/legacy-components/src/theme/theme';

import type { TableVariant } from '../types';

/**
 * Styles to be placed on the TableContainer when using RCTable.
 */
const RCTableContainerStyles = {
  '.rc-table-ping-left': {
    '.rc-table-cell-fix-left-last': {
      _after: {
        opacity: 1,
      },
    },
  },
  '.rc-table-ping-right': {
    '.rc-table-cell-fix-right-first': {
      _after: {
        opacity: 1,
      },
    },
  },
};

/**
 * Style shortcut to give rows with the 'clickable' class appropriate styling.
 */
const ClickableClassRowStylesFunc = (variant: TableVariant) => ({
  'tr.clickable td': {
    cursor: 'pointer',
    transitionProperty: 'common',
    transitionDuration: 'normal',
  },
  'tr.clickable:hover td': {
    bg: 'interaction.surfaceHover',
  },
  'tr.clickable:active td': {
    bg: 'interaction.surfaceActive',
  },

  // Ensure border float fix respects clickable rows.
  'tr.clickable:hover td.rc-table-cell-fix-left-last': {
    borderRight: `1px solid ${getColor(
      theme,
      variant !== 'simple' ? 'separator.divider' : 'interaction.surfaceHover',
    )}!important`,
  },
  'tr.clickable:active td.rc-table-cell-fix-left-last': {
    borderRight: `1px solid ${getColor(
      theme,
      variant !== 'simple' ? 'separator.divider' : 'interaction.surfaceActive',
    )}!important`,
  },
});

/**
 * Style shortcut to give expanded rows shadows.
 */
const ExpandedRowShadows = {
  '.rc-table-expanded-row .rc-table-cell': {
    boxShadow:
      'inset 0 0.5rem 0.5rem -0.5rem rgba(0, 0, 0, 0.25), inset 0 -0.5rem 0.5rem -0.5rem rgba(0, 0, 0, 0.25)',
  },
};

/**
 * Styles to be placed on the RCTable.
 */
const RCTableStylesFunc = (variant: TableVariant) => ({
  width: '100%',
  '.rc-table-cell-fix-left': {
    zIndex: 2,
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: '0px',
  },
  '.rc-table-sticky-header': {
    position: 'sticky',
    zIndex: 3,
  },
  'td:not(.rc-table-cell-fix-left)': {
    zIndex: 1,
  },
  'td.rc-table-cell-fix-left': {
    zIndex: 2,
  },

  // Add cell border back (same color as background) for left side cells to prevent text floating above fixed columns in the 1px border space.
  // ------------------------------------------------------------------ //
  'td.rc-table-cell-fix-left-last': {
    borderRight: `1px solid ${getColor(
      theme,
      variant !== 'simple' ? 'separator.divider' : 'page.surface',
    )}!important`,
  },
  'th.rc-table-cell-fix-left-last': {
    borderRight: `1px solid ${getColor(
      theme,
      variant !== 'simple' ? 'separator.divider' : 'page.plateLight',
    )}!important`,
  },
  // ------------------------------------------------------------------ //

  '.rc-table-cell-fix-left-last': {
    _after: {
      position: 'absolute',
      top: 0,
      bottom: '-1px',
      right: '-1px',
      width: '21px',
      transform: 'translateX(100%)',
      transition: 'opacity .3s',
      content: `""`,
      pointerEvents: 'none',
      opacity: 0,
      background:
        'linear-gradient(-90deg,rgba(0, 0, 0, 0) 45%,rgba(0, 0, 0, 0.03) 70%,rgba(0, 0, 0, 0.15) 100%)',
    },
  },

  '.rc-table-cell-fix-right-first': {
    _after: {
      position: 'absolute',
      top: 0,
      bottom: '-1px',
      left: '-1px',
      width: '20px',
      transform: 'translateX(calc(-100% + 1px))',
      transition: 'opacity .3s',
      content: `""`,
      pointerEvents: 'none',
      opacity: 0,
      borderRight: variant !== 'simple' ? '1px solid' : 'none',
      borderColor: 'separator.divider',
      background:
        'linear-gradient(90deg,rgba(0, 0, 0, 0) 45%,rgba(0, 0, 0, 0.03) 70%,rgba(0, 0, 0, 0.15) 100%)',
    },
  },

  // Our addition to ellipsizing stylesheet to ensure header cells also ellipsize with content.
  'th.rc-table-cell.rc-table-cell-ellipsis': {
    maxWidth: '1px',
  },

  // Styling from RC-Table docs stylesheeet
  '.rc-table-cell.rc-table-cell-ellipsis': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  '.rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-left-first': {
    overflow: 'visible',
  },
  '.rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-left-first .rc-table-cell-content':
    {
      display: ' block',
      overflow: 'hidden',
      textOverflow: ' ellipsis',
    },

  '.rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-left-last': {
    overflow: 'visible',
  },
  '.rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-left-last .rc-table-cell-content':
    {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  '.rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-right-first .rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-right-last':
    {
      overflow: 'visible',
    },
  '.rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-right-first .rc-table-cell.rc-table-cell-ellipsis.rc-table-cell-fix-right-last .rc-table-cell-content':
    {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

  '.rc-table-expanded-row td.rc-table-cell': {
    padding: 'unset',
    height: 'unset',
    verticalAlign: 'top',

    '.rc-table-expanded-row-fixed': {
      padding: 'unset',
    },
  },

  '.rc-table-row-expand-icon-cell': {
    width: '2.5rem',
    padding: '0rem',
    textAlign: 'center',
    pb: '2px',
  },

  '.rc-table-placeholder .rc-table-cell': {
    padding: 0,
  },
});

/**
 * A hook that drives provides theme-driven styling for RCTable.
 * @param {string} `variant` the table variant to match in styling.  Be sure to also place variant on the`TableContainer`.
 * @returns {Object} `{ RCTableStyles, RCTableContainerStyles, ClickableClassRowStyles }` an object containing styles for RCTable, the TableContainer, and a helper for styling rows with the `"clickable"` class as interactive.
 */
export const useRCTableStyles = (variant: TableVariant) => {
  const RCTableStyles = RCTableStylesFunc(variant);
  const ClickableClassRowStyles = ClickableClassRowStylesFunc(variant);
  const ClickableClass = 'clickable';
  return {
    RCTableStyles,
    RCTableContainerStyles,
    ClickableClassRowStyles,
    ClickableClass,
    ExpandedRowShadows,
  };
};
